import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ProctorService } from '../services/proctor.service';
import { environment } from 'src/environments/environment';
import { CancellationToken, UtilService } from '../services/util.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-initiate-second-view-modal',
  templateUrl: './initiate-second-view-modal.component.html',
  styleUrls: ['./initiate-second-view-modal.component.css']
})
export class InitiateSecondViewModalComponent implements OnInit, AfterViewInit, OnDestroy{
  hasSecondViewCamera: boolean = false;
  @Input() secondViewPreferencePromiseResolveFunction: (value: boolean | PromiseLike<boolean>) => void;
  @Input() secondViewPreferencePromiseRejectFunction: (reason?: any) => void;
  //@Input() primaryViewWebcamDeviceId: string;
  primaryViewWebcamDeviceLabel: string;
  @Input() primaryViewInitiationData: any;
  allowExternalSecondViewCamera: boolean;
  secondViewDeviceId: string;
  showAccessCodeDiv: boolean = false;
  secondViewAccessCode: string;
  secondViewDeviceLabel: string;
  showPreferenceQuestion: boolean;
  secondViewPreference: string;
  waitingForSecondView: boolean = false;
  secondViewExternalDeviceUrl: string = `${this.proctorService.company}.${environment.secondViewProctorDomain}`;
  waitForSecondViewRecordingCancellationToken: CancellationToken = new CancellationToken();
  instructionsText: string;
  gotInteractiveSecondViewImageSnapshot: boolean = false;
  interactiveSecondViewImageSnapshotObjectUrl: string;
  rejected: boolean = false;
  candidateNo = this.proctorService.loggedInUsername;
  @ViewChild('mainVideo') mainVideo: ElementRef;
  @ViewChild('secondViewVideo') secondViewVideo: ElementRef;
  externalSecondViewCameraChosen: boolean;
  mobileSecondViewCameraChosen: boolean;
  mainVideoStream: MediaStream;
  secondViewVideoStream: MediaStream;
  setSecondViewDeviceInfoInterval: any;
  @ViewChild('interactiveSecondViewCameraImage') interactiveSecondViewCameraImage: ElementRef;
  getSecondViewSnapshotsInterval: any;
  qrCodeUrl: string;
  runningInitialSetup: boolean = true;

  constructor(private proctorService: ProctorService, private util: UtilService, private toastr: ToastrService){

  }

  ngOnDestroy(): void {
    this.mainVideoStream?.getVideoTracks()[0]?.stop();
    this.secondViewVideoStream?.getVideoTracks()[0]?.stop();  
    if(this.setSecondViewDeviceInfoInterval){
      clearInterval(this.setSecondViewDeviceInfoInterval);
    }
    if(this.getSecondViewSnapshotsInterval){
      clearInterval(this.getSecondViewSnapshotsInterval);
    }
  }

  async ngAfterViewInit(): Promise<void> {
    const _setSecondViewDeviceInfo = async () => {
      const platform = ((navigator as any).userAgentData?.platform ?? navigator.platform);
      
      if(this.allowExternalSecondViewCamera) {
        //external second view camera is not supported on mac
        //primarily because of the way the device ids change
        const secondViewDeviceInfo = await this.proctorService.getSecondViewDeviceInfo(this.primaryViewWebcamDeviceLabel);
      
        this.secondViewDeviceId = secondViewDeviceInfo?.deviceId;
        this.secondViewDeviceLabel = secondViewDeviceInfo?.deviceLabel;
        this.hasSecondViewCamera = !!this.secondViewDeviceId;
      }

      if(!this.hasSecondViewCamera && this.secondViewPreference == 'webcam'){
        this.secondViewPreference = undefined;
      }
    }

    this.runningInitialSetup = true;

    this.primaryViewWebcamDeviceLabel = this.proctorService.videoDeviceLabel;

    await this.proctorService.setSecondViewAnglesAccepted(false);

    this.allowExternalSecondViewCamera = this.proctorService.allowExternalSecondViewWebcam && !environment.isMac;

    await _setSecondViewDeviceInfo();
    this.setSecondViewDeviceInfoInterval = setInterval(async () => { 
      await _setSecondViewDeviceInfo() 
    }, 5 * 1000);
    
    this.instructionsText = 'You are required to use your mobile device ' + (this.allowExternalSecondViewCamera ? 'or an external webcam ' : '') + 'to provide a second angle for proctoring. If using an external webcam, the cable must be at least 2 meters long. The second camera must be positioned so that it shows your side view and your screen, along with the spaces beside your screen. For guidance, see the image below.';
    if(this.allowExternalSecondViewCamera){
      this.showPreferenceQuestion = true;
    }
    else{
      this.secondViewPreference = "mobile";
      this.proceedWithSecondViewPreferenceChoice(false);
    }

    this.runningInitialSetup = false;
    // if(!this.allowExternalSecondViewCamera || !this.hasSecondViewCamera){
      
    // }
    // else{
      
    // }
  }

  async ngOnInit(): Promise<void> {
    this.proctorService.secondViewPreference = undefined;
  }

  cancelModal(){
    this.reject('Dual angle proctor setup cancelled');
  }

  async generateAndShowSecondViewAccessCode(){
    try{
      this.showAccessCodeDiv = true;
      this.showPreferenceQuestion = false;
      this.secondViewAccessCode = await this.proctorService.generateSecondViewAccessCode();
      this.qrCodeUrl = `https://${this.secondViewExternalDeviceUrl}/?accessCode=${encodeURIComponent(this.secondViewAccessCode)}&candidateNo=${encodeURIComponent(this.candidateNo)}&source=qrcode`;
    }
    catch(error){
      this.toastr.error('Error while generating access code');
      this.reject(error);
      throw error;
    }
  }

  async proceedWithSecondViewPreferenceChoice(proceedClicked: boolean){
    this.showPreferenceQuestion = false;
    try{
      this.proctorService.secondViewPreference = this.secondViewPreference;
      if(this.secondViewPreference == "mobile"){
        if(!proceedClicked){
          this.mobileSecondViewCameraChosen = true;
          const primaryViewWebcamDeviceId = await this.proctorService.getDeviceId(this.primaryViewWebcamDeviceLabel);
          this.mainVideoStream = await navigator.mediaDevices.getUserMedia({
            video: {deviceId: {ideal: primaryViewWebcamDeviceId}},//had to use ideal here to get this working on mac
            audio: false
          });

          this.mainVideo.nativeElement.srcObject = this.mainVideoStream;

          await this.generateAndShowSecondViewAccessCode();

          const newerThan_Ticks = await this.proctorService.getServerTimeTicks();
          this.getSecondViewSnapshotsInterval = setInterval(async () => {
            this.interactiveSecondViewImageSnapshotObjectUrl = await this.proctorService.getSecondViewSnapshotObjectUrl(newerThan_Ticks);
            this.showAccessCodeDiv = false;
            this.gotInteractiveSecondViewImageSnapshot = true;
          }, 2000);
        }
        else{
          this.waitingForSecondView = true;

          await this.proctorService.waitForSecondViewRecording(this.waitForSecondViewRecordingCancellationToken);

          await this.proctorService.setSecondViewAnglesAccepted(true);

          this.accept(true);
        }
      }
      else if(this.allowExternalSecondViewCamera){
          //initiating the second view recording via an external webcam
          //this process sends a message to an iframe
          //the page used is /secondview
          //this promise only returns after a recording has been uploaded so we don't have to call waitForSecondViewRecording() unless we simply want to enforce consistency at the cost of an extra api call
          if(!proceedClicked){
            this.externalSecondViewCameraChosen = true;
            const primaryViewWebcamDeviceId = await this.proctorService.getDeviceId(this.primaryViewWebcamDeviceLabel);
            //here, we'll just show the video feeds so they can adjust properly
            this.mainVideoStream = await navigator.mediaDevices.getUserMedia({
              video: {deviceId: {ideal: primaryViewWebcamDeviceId}},//had to use ideal here to get this working on mac
              audio: false
            });
            this.secondViewVideoStream = await navigator.mediaDevices.getUserMedia({
              video: {deviceId: {exact: this.secondViewDeviceId}},
              audio: false
            });

            this.mainVideo.nativeElement.srcObject = this.mainVideoStream;
            this.secondViewVideo.nativeElement.srcObject = this.secondViewVideoStream;
          }
          else{
            this.waitingForSecondView = true;
            await this.proctorService.sendMessageToSecondViewIframe({ ...this.primaryViewInitiationData, isSecondViewProctor: true, secondViewDeviceLabel: this.secondViewDeviceLabel, primaryViewWebcamDeviceLabel: this.primaryViewWebcamDeviceLabel, startSecondViewProctor: false });

            //this should pretty much return immediately because the second view iframe only sends the message to fulfil the promise after the first recording has been uploaded
            await this.proctorService.waitForSecondViewRecording(this.waitForSecondViewRecordingCancellationToken);
            
            this.accept(false);
          }
      }
      else{
        console.error(`Unexpected condition: allowExternalSecondViewCamera = ${this.allowExternalSecondViewCamera}, secondViewPreference != mobile`);
        throw new Error('Unexpected condition');
      }
    }
    catch(error){
      this.reject(error);
      throw error;
    }
  }

  reject(error: any){
    this.rejected = true;
    if(this.waitForSecondViewRecordingCancellationToken){
      this.waitForSecondViewRecordingCancellationToken.cancel = true;
    }
    this.proctorService.secondViewPreference = undefined;
    this.secondViewPreferencePromiseRejectFunction(error);
  }

  accept(ret: boolean){
    this.secondViewPreferencePromiseResolveFunction(ret);
  }
}
